import { Link } from "react-router-dom";
import {
  getActivity, getHealth, getProgress,
  type AttentionItem, type Overview, type OverviewAssignment, type Progress,
} from "../api";
import { useShell } from "../Layout";
import { attentionHref, routes } from "../routes";
import { useAsync } from "../useAsync";
import {
  ago, Badge, Card, CardBody, CardHead, DASH, Empty, Freshness, Pane, State, When,
} from "../ui";
import {
  clockOrDay, dayAndTime, dayTokens, Gauge, paidDollars, pct, since, totalMinutes,
} from "../usage";
import { orderAttention, scaleSeries, segments } from "./overview-math";

/**
 * The landing page.
 *
 * 🔴 FOUR PANES, FOUR REQUESTS, AND THAT IS THE DESIGN. `/overview` is database-only and
 * always fast; `/health` blocks on Windmill and on one HTTP call per rubric; `/progress` is a
 * paginated Classroom listing measured at 5.6 s on the live cohort; `/activity` is cheap but
 * unimportant. Fetched together, the slowest would decide when anything appeared. Fetched
 * apart, a Classroom outage costs one column of one card and says so where the column was.
 *
 * 🔴 THE FIRST THING ON THE PAGE IS WHAT NEEDS A PERSON. Everything a teacher had to act on —
 * a grade waiting for review, a mark waiting to be typed in, a paid run that stored nothing, a
 * submission parked and never retried — was findable only by opening the right tab and reading
 * the right column, and three of those four are money or a student left waiting.
 */
export function OverviewPage() {
  const { courseId, overview } = useShell();
  const health = useAsync(() => getHealth(courseId), [courseId]);
  const progress = useAsync(() => getProgress(courseId), [courseId]);
  const activity = useAsync(() => getActivity(courseId, { limit: 8 }), [courseId]);

  return (
    <div className="stack">
      <div className="page-head">
        <p className="eyebrow">Cohort</p>
        <h2>Overview</h2>
        <span className="fresh"><Freshness q={overview} /></span>
      </div>

      <div className="grid ov">
        <Card>
          <CardHead
            title="Needs you"
            desc={<>Worst first. Each one opens the page it is about. A <strong>STOP</strong> is
                  money already spent or a student blocked; a <strong>WARN</strong> is a
                  decision waiting.</>} />
          <CardBody tight>
            <Pane q={overview} empty={null}>{(d) => {
              const items = orderAttention(d.attention);
              return items.length === 0
                ? <Empty title="Nothing needs you"
                         hint="No grade is waiting for review, no mark is waiting to be typed
                               in, and nothing has been paid for without producing a row." />
                : <div className="att">
                    {items.map((it, i) => <Att key={`${it.kind}-${i}`}
                                               courseId={courseId} it={it} />)}
                  </div>;
            }}</Pane>
          </CardBody>
        </Card>

        <div className="stack">
          <Card>
            <CardHead title="System"
                      desc={<>Nine readings, two of which ask a remote system.</>}
                      right={<Link className="small" to={routes.page(courseId, "health")}>
                        Health →</Link>} />
            <CardBody tight>
              <Pane q={health} empty={null}>{(h) => (
                <div className="sysstrip">
                  {h.cards.map((c) => (
                    <Link key={c.name} className="chip" to={routes.page(courseId, "health")}
                          title={String(c.detail).slice(0, 220)}>
                      <State s={c.state} />{c.name}
                    </Link>))}
                </div>)}
              </Pane>
            </CardBody>
          </Card>
          <Card><Pane q={overview} empty={null}>{(d) => <Usage d={d} />}</Pane></Card>
        </div>
      </div>

      <Card>
        <CardHead
          title="Assignments"
          desc={<><strong>Submitted</strong> is what Classroom holds — TURNED_IN plus RETURNED,
                because returned work is still attached. <strong>Graded</strong> is what this
                console holds. The bar is ours against submitted.</>}
          right={<>
            {progress.data && (
              /* ⚠️ THE SUBMITTED COLUMN IS A CACHED READING and says so. The poller refreshes
                 it every two minutes; if the poller has stopped, this is the number that goes
                 quietly stale, so it carries its own age. */
              <span className="small muted" title={`Classroom listing of ${
                progress.data.listed} submissions, ${
                progress.data.from_cache ? "from cache" : "fetched just now"}`}>
                submitted as of {ago(progress.data.age_seconds)}
              </span>)}
            <Legend />
          </>} />
        <CardBody tight>
          <Pane q={overview} empty={null}>{(d) => (
            d.assignments.length === 0
              ? <Empty title="No coursework here yet"
                       hint="Open Assignments to list them from Classroom — that is also what
                             caches their titles for this page." />
              : <div>{d.assignments.map((a) => (
                  <Row key={a.course_work_id} courseId={courseId} a={a}
                       progress={progress.data} err={!!progress.error} />))}</div>)}
          </Pane>
        </CardBody>
      </Card>

      <div className="grid ov">
        <Card>
          <CardHead title="Recent activity" desc="The last eight things that happened here."
                    right={<Link className="small" to={routes.page(courseId, "logs")}>
                      all →</Link>} />
          <CardBody tight>
            <Pane q={activity} empty={null}>{(a) => (
              a.rows.length === 0
                ? <Empty title="Nothing has happened in this cohort yet" />
                : <ul className="activity">
                    {a.rows.map((r, i) => (
                      <li className="act-row" key={`${r.at}-${i}`}>
                        <span className="when">{r.at.slice(11, 16) || DASH}</span>
                        <span>
                          {r.actor === "sweep"
                            ? <Badge tone="held">sweep</Badge>
                            : <span className="muted">{r.actor.split("@")[0]}</span>}{" "}
                          {r.text}
                        </span>
                      </li>))}
                  </ul>)}
            </Pane>
          </CardBody>
        </Card>

        <div className="stack">
          <Pane q={overview} empty={null}>{(d) => <>
            <Card className="mini-card">
              <div className="label">Queue</div>
              <div className="value">{d.queue.queued} <small>queued</small></div>
              <div className="hint">
                {d.queue.dispatched} dispatched · {sum(d.queue.parked_by_reason)} parked
                {d.queue.undrained_requests > 0 && <>
                  {" "}· <span className="warn">{d.queue.undrained_requests} request(s) not
                  yet drained</span> — a teacher pressed Run and no tick has picked it up</>}
              </div>
              <Link className="small" to={routes.page(courseId, "assignments")}>
                Assignments →</Link>
            </Card>
            <Card className="mini-card">
              <div className="label">Social activity</div>
              <div className="value">
                {d.build_log.enabled ? d.build_log.on_topic : DASH}{" "}
                <small>on topic of {d.build_log.on_topic + d.build_log.off_topic
                  + d.build_log.unclassified}</small>
              </div>
              <div className="hint">
                {d.build_log.enabled
                  ? <>Last collected <When iso={d.build_log.last_collected_at} />.{" "}
                     {d.build_log.linked} student(s) linked. Off-topic posts are counted and
                     never described.</>
                  : <>Switched off, which is the default. Nothing is fetched and nothing is
                     spent.</>}
              </div>
              <Link className="small" to={routes.page(courseId, "students")}>Students →</Link>
            </Card>
            <Card className="mini-card">
              <div className="label">Roster</div>
              <div className="value">{d.roster.students} <small>students</small></div>
              <div className="hint">
                {d.roster.note
                  ? d.roster.note
                  : <>Synced <When iso={d.roster.last_synced} /> · {d.roster.named} named.</>}
              </div>
              <Link className="small" to={routes.page(courseId, "students")}>Students →</Link>
            </Card>
          </>}</Pane>
        </div>
      </div>
    </div>
  );
}

const sum = (o: Record<string, number>) => Object.values(o).reduce((a, b) => a + b, 0);

function Att({ courseId, it }: { courseId: string; it: AttentionItem }) {
  return (
    <Link className={it.severity === "STOP" ? "att-item stop" : "att-item"}
          to={attentionHref(courseId, it)}>
      <span className="sev" />
      <span className="n">{it.count}</span>
      <span className="body">
        {/* ⚠️ THE WORD AS WELL AS THE STRIPE. Colour does not survive a screenshot and never
            existed for a screen reader. */}
        <span className="t"><State s={it.severity} />{it.title}</span>
        <span className="m">{it.detail}</span>
      </span>
      <span className="go">→</span>
    </Link>
  );
}

function Legend() {
  return (
    <span className="legend">
      <span><i className="swatch" style={{ background: "var(--tb-ok)" }} />released or entered</span>
      <span><i className="swatch"
               style={{ background: "color-mix(in srgb, var(--tb-ok) 42%, transparent)" }} />ready</span>
      <span><i className="swatch" style={{ background: "var(--tb-warn)" }} />review</span>
      <span><i className="swatch" style={{ background: "var(--border-strong)" }} />held</span>
    </span>
  );
}

function Row({ courseId, a, progress, err }:
  { courseId: string; a: OverviewAssignment; progress: Progress | null; err: boolean }) {
  const p = progress?.by_assignment[a.course_work_id];
  // ⚠️ THE DENOMINATOR IS WHAT CLASSROOM SAYS WAS SUBMITTED. Until that arrives, fall back to
  // what we hold, so the bar is honest about being a partial picture rather than empty.
  const total = p?.submitted ?? a.ours.graded;
  const segs = segments([
    { key: "done", value: a.ours.released + a.ours.entered },
    { key: "ready", value: Math.max(0, a.ours.ready - a.ours.entered) },
    { key: "review", value: a.ours.review },
    { key: "held", value: a.ours.held },
  ], total);
  const parked = sum(a.queue.parked);

  return (
    <div className="progress-row">
      <div>
        <div className="title">
          {a.title || <span className="muted">{a.course_work_id}</span>}
        </div>
        <div className="sub">
          {a.due ? <>due {a.due}</> : <span className="muted">no due date</span>}
          <Badge tone="plain">{a.effective_engine}</Badge>
          {a.effective_auto_grade && <Badge tone="warn" dot title="Unattended grading is armed
for this assignment AND the cohort switch is on. Each alone does nothing.">auto</Badge>}
          {!a.cached && <Badge tone="held" title="This console has not listed this coursework
from Classroom yet, so it has no title. Open Assignments once.">uncached</Badge>}
        </div>
      </div>
      <div>
        <div className="bar-stack"
             title={`${total} submitted · ${a.ours.graded} graded here`}>
          {segs.map((s) => s.pct > 0 &&
            <i key={s.key} className={`bar-seg ${s.key}`} style={{ width: `${s.pct}%` }} />)}
        </div>
        <div className="nums">
          {p ? <><b>{p.submitted}</b> submitted · </>
             : err ? <span className="warn">Classroom unavailable · </span> : null}
          <b>{a.ours.graded}</b> graded here
          {a.ours.review > 0 && <> · <b>{a.ours.review}</b> review</>}
          {a.ours.released + a.ours.entered > 0 &&
            <> · <b>{a.ours.released + a.ours.entered}</b> released or entered</>}
          {a.ours.held > 0 && <> · <b>{a.ours.held}</b> held</>}
          {parked > 0 && <> · <span className="warn">{parked} parked</span></>}
          {p && p.marked_in_classroom > 0 &&
            <> · {p.marked_in_classroom} marked in Classroom, not by us</>}
        </div>
      </div>
      <Link className="btn sm" to={routes.assignment(courseId, a.course_work_id)}>Open</Link>
    </div>
  );
}

/**
 * The subscription grading runs on: its weekly figure against the line where unattended grading
 * pauses, the 5-hour window, today's runs against the backstop, and a week of runs per day.
 *
 * 🔴 USAGE, NOT A PRICE (owner, 2026-10-03). This card was Spend — dollars against a daily cap —
 * and on a subscription that figure was a price nobody pays. Every number here is the Health
 * card's own reading (`overview.usage.grading` is built from it), so the figure a teacher reads
 * to decide is the figure the poller pauses on and the count dispatch parks on.
 * ⚠️ FOUR STATES, NOT ONE CARD WITH BLANKS: a reading, a reading past the pause line, no reading
 * yet, and a reading whose weekly window has reset since — which is no reading at all, because
 * the figure it held is no longer true and never pauses anything. None of the last two is 0%.
 * ⚠️ AND A READING MAY CARRY ONE WINDOW (the server keeps any single valid one), so "not read"
 * is said of a window, never assumed of the reading: a 5-hour figure a run did read is shown
 * whether or not the weekly one came with it.
 * 🔴 SOCIAL ACTIVITY KEEPS ITS DOLLARS, in the footnote, IN EVERY STATE: that service is paid per
 * call, and the old Spend card always showed it. Gated on a fresh weekly reading it would never
 * appear on the estate as it is — paused at 99%, or not read since the update.
 */
/** What is said of today's runs that measured nothing: `1 still running`, `1 reported nothing`.
 *  🔴 NEVER A ZERO. A run in flight counts against the backstop and has no envelope yet, so its
 *  minutes and tokens are not 0 — they are not known. `running_now` is the cohort's own count
 *  of runs in flight; a run started before midnight is in it and not in today's runs, hence the
 *  `min`. Whatever is left ended before the grader reported anything. */
function unmeasuredToday(day: { runs: number; measured: number },
                                runningNow: number): string[] {
  const unmeasured = Math.max(0, day.runs - day.measured);
  const running = Math.min(Math.max(0, runningNow), unmeasured);
  const silent = unmeasured - running;
  return [running > 0 && `${running} still running`,
          silent > 0 && `${silent} reported nothing`].filter((s): s is string => !!s);
}

function Usage({ d }: { d: Overview }) {
  const g = d.usage.grading;
  const now = Date.now();
  const weekly = g.weekly && !g.weekly.expired ? g.weekly : null;
  const five = g.five_hour && !g.five_hour.expired ? g.five_hour : null;
  const series = d.usage.series;
  const today = series[series.length - 1];
  const heights = scaleSeries(series.map((s) => s.runs));
  const plural = (n: number) => (n === 1 ? "run" : "runs");
  const full = g.runs_per_day > 0 && g.runs_today >= g.runs_per_day;
  // 🔴 WHAT PRESSING RUN DOES, from the facts the server's sentence is built from
  // (`subscription_usage.teacher_run`): it skips the weekly pause and nothing else. [X] It said
  // "still goes" on 2026-10-04 while the subscription's own limit held every run until 19:02.
  const teacherRun = g.run_held_until
    ? <>A teacher’s Run is held too, until <b>{dayAndTime(g.run_held_until)}</b>: the subscription
        itself refused a run.</>
    : g.runs_per_day <= 0
      ? <>A teacher’s Run is held too: the runs-per-day backstop is 0, which means stop.</>
      : full
        ? <>A teacher’s Run is held too, until midnight UTC: today’s runs-per-day backstop is
            reached.</>
        : <>A teacher’s Run still goes — until the subscription itself says no.</>;
  return (
    <>
      <CardHead title="Usage"
                desc="The Claude subscription grading runs on — every cohort, and your own Claude use, count toward it."
                right={g.paused
                  ? <Badge tone="warn" dot>unattended grading paused</Badge>
                  : weekly && <span className="small muted r">
                      {g.running_now} of {g.max_in_flight} running</span>} />
      <CardBody>
        <div className="stack tight">
          {weekly
            ? <div className="use-big">{pct(weekly.pct)} <small>of the weekly allowance</small></div>
            : <div className="use-big none">{DASH} <small>{g.weekly
                ? "weekly usage not read since it reset"
                : g.read ? "weekly usage not in the last reading"
                : "weekly usage not read yet"}</small></div>}
          <Gauge pct={weekly?.pct ?? null} pause={g.pause_pct} over={g.paused} />
          {(weekly || five) && (
            <div className="use-split small">
              {weekly && <span>resets <b>{dayAndTime(weekly.resets_at)}</b></span>}
              <span className="muted">5-hour window {five
                ? <><b className="num">{pct(five.pct)}</b> · resets {clockOrDay(five.resets_at, now)}</>
                : g.five_hour ? "not read since it reset" : "not read"}</span>
            </div>)}
          {g.paused
            ? (g.pause_pct <= 0
                ? <div className="small"><b>The weekly pause is set to 0%, which means stop:
                    nothing new starts on its own.</b> {teacherRun}</div>
                : <div className="small"><b>Nothing new starts on its own until weekly usage is
                    under {pct(g.pause_pct)}.</b> {teacherRun}</div>)
            : !weekly && (
                <div className="small">
                  {g.weekly
                    ? <>The weekly window reset on {dayAndTime(g.weekly.resets_at)}; the next run
                        reads it again.</>
                    : g.read
                      ? <>The last reading carried no weekly figure; the next run reads it
                          again.</>
                      : <>The first run on the updated grader reads it.</>}{" "}
                  Until then, the runs-per-day backstop is what stops unattended grading.
                </div>)}
          {!g.paused && (
            <div className="small">
              Grading today in this cohort:{" "}
              <span className={full || g.runs_per_day <= 0 ? "warn" : undefined}>
                <b className="num">{g.runs_today}</b> {plural(g.runs_today)} of{" "}
                <b className="num">{g.runs_per_day}</b></span>
              {/* ⚠️ ONLY WHAT WAS MEASURED. A run still going, or one that ended before the
                  grader reported anything, has no minutes or tokens; a day of only those used
                  to read "0 min · 0 tokens", a measured zero for runs nobody had measured. So
                  the figures are the measured runs' alone, and the rest are SAID: running, or
                  reported nothing. */}
              {today && g.runs_today > 0 && today.measured > 0 && <>
                {" "}· <b className="num">{totalMinutes(today.minutes)}</b>
                {" "}· <b className="num">{dayTokens(today.tokens)}</b> tokens</>}
              {today && unmeasuredToday(today, g.running_now).map((said) => (
                <span key={said} className="muted"> · {said}</span>))}
              {/* 🔴 0 MEANS STOP, NEVER UNLIMITED — and a backstop reached is said where it
                  is counted, or a quiet afternoon reads as a stopped poller. */}
              {g.runs_per_day <= 0
                ? <span className="warn"> — 0 means stop: every run is parked, a teacher’s
                    included.</span>
                : full && <span className="warn"> — the backstop is reached: nothing more starts
                    in this cohort today (UTC).</span>}
            </div>)}
          {!g.paused && weekly && (
            <div>
              <div className="spark" aria-label="Grading runs per day, last seven days">
                {series.map((s, i) => (
                  <div key={s.day}
                       className={`spark-bar${s.runs <= 0 ? " zero" : ""}${
                         i === series.length - 1 ? " today" : ""}`}
                       style={{ height: `${Math.max(2, (heights[i] ?? 0) * 100)}%` }}
                       title={s.runs <= 0 ? `${s.day} · 0 run(s) · nothing ran`
                         : [`${s.day} · ${s.runs} run(s)`,
                            ...(s.measured > 0
                              ? [`${dayTokens(s.tokens)} tokens`, totalMinutes(s.minutes)] : []),
                            // Only today has runs in flight; an earlier day's unmeasured runs
                            // ended without reporting.
                            ...unmeasuredToday(s, i === series.length - 1 ? g.running_now : 0),
                           ].join(" · ")} />))}
              </div>
              <div className="spark-lbl">
                {series.map((s) => <span key={s.day}>{s.day.slice(8)}</span>)}
              </div>
            </div>)}
          <div className="small muted">
            {(weekly || five) && g.read_at && <>
              Read by the run that finished {since(g.read_at, now)}.{" "}</>}
            Social activity:{" "}
            <b className="num">{paidDollars(d.usage.build_log.today_est_usd)}</b> estimated
            of {paidDollars(d.usage.build_log.cap_usd)} today — paid per call, so it stays in
            dollars.
          </div>
        </div>
      </CardBody>
    </>
  );
}

